import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart3,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Mail,
  MailWarning,
  Megaphone,
  Menu,
  Scale,
  ShieldCheck,
  Store,
  Users,
  UsersRound,
  X,
} from "lucide-react";
import { Footer } from "@bc/ui";

import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/payments", label: "Payments", icon: CreditCard },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/verification", label: "Verification", icon: ShieldCheck },
  { to: "/conveyancers", label: "Conveyancers", icon: Scale },
  { to: "/buyers", label: "Buyers", icon: UsersRound },
  { to: "/sellers", label: "Sellers", icon: Store },
  { to: "/users", label: "Users", icon: Users },
  { to: "/send-email", label: "Send Email", icon: Mail },
  { to: "/marketing-email", label: "Marketing Email", icon: Megaphone },
  { to: "/failed-emails", label: "Failed Emails", icon: MailWarning },
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef(null);
  const { user, logout } = useAuth();

  // The <main> pane is the only scroll container in the admin frame, and it
  // keeps its scroll position across route changes — so a new page would open
  // wherever the previous one was scrolled to. Reset it on navigation.
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };

  return (
    // 100dvh tracks the *visible* mobile viewport (URL bar shown or hidden),
    // so the frame never exceeds the screen and the body never scrolls —
    // which is what lets the top bar scroll away on mobile. The h-screen
    // class remains as the fallback for browsers without dvh support.
    <div
      className="h-screen flex flex-col overflow-hidden bg-gray-50"
      style={{ height: "100dvh" }}
    >
      {/* Top bar — navy chrome, never scrolls away */}
      <header className="sticky top-0 bg-darkblue border-b border-white/10 z-40 flex-shrink-0">
        <div className="flex items-center justify-between gap-2 h-16 px-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md text-white hover:bg-white/10 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <img
              src="/bc-logo-transparent.png"
              alt="BidConvey"
              className="h-10 w-auto object-contain cursor-pointer brightness-0 invert"
              onClick={() => navigate("/dashboard")}
            />
            <span className="text-white/70 text-xs font-semibold uppercase tracking-wide hidden sm:inline">
              Admin
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="bg-transparent hover:bg-white/5 text-white hover:text-white h-9 text-xs font-semibold border border-gold gap-1.5 flex-shrink-0"
            data-testid="logout-btn"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar — navy, fixed, does not scroll with the content */}
        <aside
          className={`
            fixed top-16 left-0 bottom-0 w-64 bg-darkblue border-r border-white/10
            z-30 transform transition-transform duration-200 ease-in-out overflow-y-auto
            lg:static lg:translate-x-0 lg:z-auto lg:flex-shrink-0
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          `}
        >
          <nav className="p-3 space-y-1">
            {NAV_ITEMS.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                    active
                      ? "text-gold font-semibold"
                      : "text-white hover:text-gold"
                  }`}
                >
                  <item.icon className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1">{item.label}</span>
                </Link>
              );
            })}
          </nav>
          {user && (
            <p className="px-6 pb-4 text-[10px] uppercase tracking-wider text-white/50 truncate">
              {user.email}
            </p>
          )}
        </aside>

        {/* Main content — only this scrolls */}
        {/* min-w-0: without it this flex child refuses to shrink below its
            content width, letting a wide table push the whole page sideways. */}
        <main ref={mainRef} className="flex-1 min-w-0 overflow-y-auto">
          {children}
        </main>
      </div>

      {/* Footer — pinned inside the viewport frame, below sidebar + content */}
      <div className="flex-shrink-0">
        <Footer />
      </div>
    </div>
  );
}
